import Link from '@teetsh/app/src/components/Link';
import Button from '@teetsh/app/src/components/Buttons/Button';
import Card from '@teetsh/app/src/components/Card';
import { useCountryConfig } from '@teetsh/app/src/queries/user/useCountryConfig';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import { PlusIcon as Plus } from '@heroicons/react/20/solid';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import ButtonExternal from '@teetsh/app/src/components/Buttons/ExternalLink';
import _sortBy from 'lodash-es/sortBy';
import React, { ComponentType } from 'react';
import DemoImage from 'components/DemoImage';
import { useTranslation } from 'react-i18next';
import ButtonLink from 'components/Buttons/Link';
import { TrashIcon } from '@heroicons/react/24/outline';
import { IllustratedPage } from '@teetsh/app/src/components/IllustratedPage';

interface Item {
  id: string;
  title: string;
  type?: string;
  countSubItems?: number;
  icon?: React.ReactNode;
  mergeableWith?: { id: string; title: string }[];
  subtitle?: string | React.ReactNode;
  link: string;
}

interface Props {
  items: Item[];
  tutorialLink?: string;
  onClickItem?: (itemId: string) => void;
  title: string;
  onAddItem?: () => void;
  small?: boolean;
  children?: React.ReactNode;
  newTitle: string;
  newDescription: string;
  newButtonCtaLabel: string;
  shortNewButtonCtaLabel?: string;
  illustration?: any;
  screenshotUrl?: string;
  hideItemsSubtitleOnMobile?: boolean;
  Actions: ComponentType<{
    id: string;
    title: string;
    type?: string;
    countSubItems?: number;
    mergeableWith?: { id: string; title: string }[];
  }>;
  deletedItemsHref?: string;
}

const StyledCard = styled(Card)(({ small }: { small?: boolean }) => [
  small && tw`py-0! mb-2`,
  !small && tw`mb-4`,
]);

export function ItemsList(props: Props) {
  const {
    title,
    small,
    onAddItem,
    items,
    onClickItem,
    Actions,
    newButtonCtaLabel,
    shortNewButtonCtaLabel,
    newDescription,
    screenshotUrl,
    newTitle,
    tutorialLink,
    illustration,
    hideItemsSubtitleOnMobile = true,
    children,
    deletedItemsHref,
  } = props;
  const { t } = useTranslation(['common']);
  const { areOnboardingTutoEnabled } = useCountryConfig();

  if (items.length === 0) {
    return (
      <IllustratedPage
        illustration={illustration}
        title={newTitle}
        description={newDescription}
      >
        <div tw="inline-flex justify-center">
          <Button
            aria-label="Nouveau"
            intent="primary"
            fill
            large
            onClick={onAddItem}
            icon={Plus}
          >
            <span css={[shortNewButtonCtaLabel && tw`hidden sm:inline-flex`]}>
              {newButtonCtaLabel}
            </span>
            {shortNewButtonCtaLabel && (
              <span tw="sm:hidden">{shortNewButtonCtaLabel}</span>
            )}
          </Button>
        </div>
        {deletedItemsHref && (
          <ButtonLink to={deletedItemsHref} intent="none" large tw="w-full">
            {t('common:buttons.seeTrash')}
          </ButtonLink>
        )}
        {areOnboardingTutoEnabled && tutorialLink && (
          <div>
            <ButtonExternal
              target="_blank"
              href={tutorialLink}
              intent="none"
              large
              fill
              icon={ArrowTopRightOnSquareIcon}
            >
              {t('common:buttons.openTutoShort')}
            </ButtonExternal>
          </div>
        )}
        {screenshotUrl && (
          <DemoImage imageUrl={screenshotUrl} tw="my-12 max-w-5xl mx-auto" />
        )}
      </IllustratedPage>
    );
  }
  return (
    <div tw="w-full px-4 lg:px-6" aria-label={`prog-card-${title}`}>
      <div tw="mb-4 md:mb-8 py-6 flex items-center gap-y-4 flex-col lg:flex-row">
        <h1 tw="mx-4 display grow text-center text-balance lg:pl-32">
          {title}
        </h1>
        {onAddItem && (
          <div tw="lg:w-32 flex justify-end">
            <Button
              aria-label="Nouveau"
              intent="primary"
              large
              onClick={onAddItem}
              icon={Plus}
            >
              {t('common:buttons.add')}
            </Button>
          </div>
        )}
      </div>
      <div tw="mb-4 md:mb-8 max-w-lg mx-auto">{children}</div>
      <div tw="mb-6 max-w-4xl mx-auto">
        {_sortBy(items, i => i.title.toLowerCase()).map((item, index) => (
          <StyledCard
            role="link"
            tabIndex={index}
            key={item.id}
            interactive
            small={small}
            aria-label={`Aller sur ${title} ${index}`}
            onClick={onClickItem ? () => onClickItem(item.id) : undefined}
            tw="p-4! md:px-6! rounded-lg! border-transparent! hover:border-neutral-moderate!"
          >
            <div tw="flex justify-between items-center gap-x-2">
              <div
                css={[tw`my-0`, !small && tw`text-lg`, small && tw`font-bold`]}
              >
                <Link to={item.link}>
                  <div
                    css={[
                      tw`text-neutral-moderate font-bold text-base`,
                      item.icon && tw`inline-flex items-center`,
                    ]}
                  >
                    {item.icon && <span tw="shrink-0 mr-2">{item.icon}</span>}
                    {item.title}
                  </div>
                  {item.subtitle && (
                    <div
                      css={[
                        tw`text-muted text-sm`,
                        hideItemsSubtitleOnMobile && tw`hidden md:block`,
                      ]}
                    >
                      {item.subtitle}
                    </div>
                  )}
                </Link>
              </div>
              <div
                role="presentation"
                tw="flex items-center shrink-0 ml-auto"
                onClick={e => e.stopPropagation()}
              >
                <Actions
                  id={item.id}
                  title={item.title}
                  type={item.type}
                  mergeableWith={item.mergeableWith}
                  countSubItems={item.countSubItems}
                />
              </div>
            </div>
          </StyledCard>
        ))}

        {deletedItemsHref && (
          <div tw="text-center">
            <ButtonLink to={deletedItemsHref} intent="none" icon={TrashIcon}>
              {t('common:buttons.seeDeletedItems')}
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  );
}
